<template>
  <div ref="container" class="container"></div>
</template>
<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { HeatmapResponse, HeatmapValue } from '@/api-client/models/utilization/Contracts'
import * as d3 from 'd3'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const dayRecords: Record<string, string> = {
  MONDAY: t('general__text__weekdays-monday'),
  TUESDAY: t('general__text__weekdays-tuesday'),
  WEDNESDAY: t('general__text__weekdays-wednesday'),
  THURSDAY: t('general__text__weekdays-thursday'),
  FRIDAY: t('general__text__weekdays-friday'),
  SATURDAY: t('general__text__weekdays-saturday'),
  SUNDAY: t('general__text__weekdays-sunday')
}

const hourRecords: Record<number, string> = {
  0: '00:00 - 01:00',
  1: '01:00 - 02:00',
  2: '02:00 - 03:00',
  3: '03:00 - 04:00',
  4: '04:00 - 05:00',
  5: '05:00 - 06:00',
  6: '06:00 - 07:00',
  7: '07:00 - 08:00',
  8: '08:00 - 09:00',
  9: '09:00 - 10:00',
  10: '10:00 - 11:00',
  11: '11:00 - 12:00',
  12: '12:00 - 13:00',
  13: '13:00 - 14:00',
  14: '14:00 - 15:00',
  15: '15:00 - 16:00',
  16: '16:00 - 17:00',
  17: '17:00 - 18:00',
  18: '18:00 - 19:00',
  19: '19:00 - 20:00',
  20: '20:00 - 21:00',
  21: '21:00 - 22:00',
  22: '22:00 - 23:00',
  23: '23:00 - 00:00'
}
const dataParser = (source: HeatmapResponse) => {
  const result: { x: number; y: number; value: any }[] = []
  for (let i = 0; i < source.heatmap.length; i++) {
    for (let j = 0; j < source.heatmap[i].length; j++) {
      result.push({
        x: i,
        y: j,
        value: source.heatmap[i][j]
      })
    }
  }
  return result
}

type Props = {
  data: HeatmapResponse
  xAccessKey: keyof HeatmapValue
  colorMin: string
  colorMax: string
}
const props = defineProps<Props>()
const container = ref<HTMLDivElement>()
const layout = ref({
  margins: {
    top: 30,
    right: 10,
    bottom: 60,
    left: 110
  },
  containerHeight: 60 + 32 * props.data.dimensions.y.length - 10,
  containerWidth: 600
})

onMounted(() => {
  // Check if the container element exists
  // If not, throw an error
  // This should never happen so that's why we can confidently throw an error
  if (!container.value) {
    throw new Error('Container element not found')
  }
  const rootElement = container.value
  const {
    data: { dimensions },
    colorMin,
    colorMax
  } = props
  /**
   * Description: This function is called when the window is resized.
   */
  const updateDimensions = async () => {
    const rootElementRect = rootElement.getBoundingClientRect()
    const margins = layout.value.margins
    const containerHeight = rootElementRect.height - margins.top - margins.bottom
    const containerWidth = rootElementRect.width - margins.left - margins.right
    layout.value = {
      ...layout.value,
      containerWidth,
      containerHeight
    }
  }

  const valueAccessor = (d: { x: number; y: number; value: any }) => {
    const key = props.xAccessKey
    const numberOfSpaces = props.data.numberOfSpaces
    const numerator = d.value[key]
    const denominator = d.value.numberOfHours
    const quotient = numerator / denominator
    const factor = numberOfSpaces
    const result = quotient / factor
    return Number.isNaN(result) ? 0 : result
  }

  const valueFormatter = (d: any) => {
    if (Number.isNaN(d) || d === 0) return '0%'
    return `${Math.round(d * 100)}%`
  }

  updateDimensions()

  const { margins, containerHeight, containerWidth } = layout.value

  let svg = d3
    .select(rootElement)
    .append('svg')
    .attr('width', containerWidth + margins.left + margins.right)
    .attr('height', containerHeight + margins.top + margins.bottom)
  let defs = svg.append('defs')
  let gradient = defs
    .append('linearGradient')
    .attr('id', `${props.xAccessKey}-gradient`)
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '100%')
    .attr('y2', '0%')
  gradient.append('stop').attr('offset', '0%').attr('stop-color', colorMin)
  gradient.append('stop').attr('offset', '100%').attr('stop-color', colorMax)

  let cardGroup = svg.append('g').attr('class', 'card')
  let legendGroup = svg.append('g').attr('class', 'legend')
  let xAxisGroup = svg.append('g').attr('class', 'x-axis')
  let yAxisGroup = svg.append('g').attr('class', 'y-axis')

  onUnmounted(() => {
    if (svg) {
      svg.remove()
    }
    window.removeEventListener('resize', () => updateDimensions())
  })

  const updateHeatmap = (data: any[]) => {
    const { margins, containerHeight, containerWidth } = layout.value
    const xScale = d3.scaleBand().domain(dimensions.x).range([0, containerWidth])
    const yScale = d3.scaleBand().domain(dimensions.y).range([0, containerHeight])
    const colorScale = d3
      .scaleLinear()
      .domain([d3.min(data, valueAccessor), d3.max(data, valueAccessor)])
      .range([colorMin, colorMax])
    svg.attr('width', containerWidth + margins.left + margins.right)
    cardGroup.attr('width', containerWidth).attr('height', containerHeight)

    cardGroup
      .selectAll('rect')
      .data(data)
      .join(
        (enter) =>
          enter
            .append('rect')
            .attr('width', xScale.bandwidth())
            .attr('height', yScale.bandwidth())
            .attr(
              'transform',
              (d) =>
                `translate(${d.x * xScale.bandwidth() + margins.left}, ${
                  d.y * yScale.bandwidth() + margins.top
                })`
            )
            .attr('stroke', '#E0E0E0')
            .attr('fill', colorMin),
        (update) => update,
        (exit) => exit.transition().duration(800).attr('fill', colorMin).remove()
      )
      .attr(
        'transform',
        (d) =>
          `translate(${d.x * xScale.bandwidth() + margins.left}, ${
            d.y * yScale.bandwidth() + margins.top
          })`
      )
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .transition()
      .duration(800)
      .attr('fill', (d) => colorScale(valueAccessor(d)))

    cardGroup
      .selectAll('text')
      .data(data)
      .join((enter) =>
        enter
          .append('text')
          .attr(
            'transform',
            (d) =>
              `translate(${d.x * xScale.bandwidth() + margins.left}, ${
                d.y * yScale.bandwidth() + margins.top
              })`
          )
          .attr('dx', xScale.bandwidth() / 2)
          .attr('dy', xScale.bandwidth() / 2)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
      )
      .attr(
        'transform',
        (d) =>
          `translate(${d.x * xScale.bandwidth() + margins.left}, ${
            d.y * yScale.bandwidth() + margins.top
          })`
      )
      .attr('dx', xScale.bandwidth() / 2)
      .attr('dy', yScale.bandwidth() / 2)
      .style('font-family', 'Inter')
      .style('font-type', 'normal')
      .style('font-weight', '400')
      .style('font-size', '13px')
      .style('line-height', '16px')
      .style('color', '#1a1a1a')
      .text((d) => valueFormatter(valueAccessor(d)))

    legendGroup
      .attr('width', containerWidth)
      .attr('height', 40)
      .attr('transform', `translate(${margins.left}, ${margins.top + containerHeight + 10})`)

    legendGroup
      .selectAll('rect')
      .data([0])
      .join((enter) =>
        enter.append('rect').attr('fill', 'url(#' + `${props.xAccessKey}-gradient` + ')')
      )
      .attr('width', containerWidth)
      .attr('height', 20)

    const minimum = d3.min(data, valueAccessor)
    legendGroup
      .selectAll('#legend-min')
      .data([0])
      .join((enter) =>
        enter
          .append('text')
          .attr('id', 'legend-min')
          .attr('text-anchor', 'start')
          .attr('dominant-baseline', 'top')
      )
      .attr('transform', `translate(${0}, ${40})`)
      .style('font-family', 'Inter')
      .style('font-style', 'normal')
      .style('font-size', '10px')
      .style('line-height', '12px')
      .style('font-weight', '400')
      .style('color', '#1a1a1a')
      .text(valueFormatter(minimum))

    const maximum = d3.max(data, valueAccessor)
    legendGroup
      .selectAll('#legend-max')
      .data([0])
      .join((enter) =>
        enter
          .append('text')
          .attr('id', 'legend-max')
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'top')
      )
      .attr('transform', `translate(${containerWidth}, ${40})`)
      .style('font-family', 'Inter')
      .style('font-style', 'normal')
      .style('font-size', '10px')
      .style('line-height', '12px')
      .style('font-weight', '400')
      .style('color', '#1a1a1a')
      .text(valueFormatter(maximum))

    const xAxis = d3
      .axisTop(xScale)
      .tickFormat((d) => dayRecords[d])
      .tickSize(0)
      .tickPadding([10])

    xAxisGroup
      .call(xAxis)
      .attr('transform', `translate(${margins.left}, ${margins.top})`)
      .call((g) => g.select('.domain').remove())

    xAxisGroup
      .selectAll('text')
      .style('font-family', 'Inter')
      .style('font-style', 'normal')
      .style('font-weight', '500')
      .style('font-size', '10px')
      .style('line-height', '12px')
      .style('color', '#1a1a1a')

    const yAxis = d3
      .axisLeft(yScale)
      .tickFormat((d) => hourRecords[d])
      .tickSize(0)
      .tickPadding([10])
    yAxisGroup
      .call(yAxis)
      .attr('transform', `translate(${margins.left}, ${margins.top})`)
      .call((g) => g.select('.domain').remove())
      .selectAll('.tick')
      .append('line')
      .attr('width', '100px')
      .attr('x1', 0)
      .attr('y1', yScale.bandwidth() / 2)
      .attr('x2', -(margins.left - margins.right))
      .attr('y2', yScale.bandwidth() / 2)
      .style('stroke', '#E0E0E0')
      .style('stroke-width', 1)

    yAxisGroup
      .selectAll('text')
      .style('font-family', 'Inter')
      .style('font-style', 'normal')
      .style('font-weight', '400')
      .style('font-size', '13px')
      .style('line-height', '16px')
      .style('color', '#1a1a1a')
  }
  // Add event listener for window resize
  window.addEventListener('resize', () => {
    updateDimensions()
    const data = dataParser(props.data)
    updateHeatmap(data)
  })
  // Watch for changes in the data and update the chart
  watch(props, (new_value) => {
    const updatedData = dataParser(new_value.data)
    updateHeatmap(updatedData)
  })
  // Initial update
  const initialData = dataParser(props.data)
  updateHeatmap(initialData)
})
</script>

<style lang="css" scoped>
#heatmap {
  text-align: center;
}
.container {
  height: 100%;
  width: 100%;
  position: relative;
}
</style>
